import { useState, useEffect, useRef } from "react";
import { getInstagramAccountId, getInstagramStats } from "../services/instagramService";

const POLL_MS = 60 * 1000;

/**
 * Resolves the Instagram Business Account ID from the Facebook Page, then
 * polls Instagram stats every 60 seconds.
 * Returns { configured, loading, followersCount, mediaCount, followsCount, error }.
 * The IG account ID is fetched once and cached in a ref for the component lifetime.
 */
export function useInstagramStats(pageId, token) {
  const [state, setState] = useState({ configured: false, loading: !!(pageId && token) });
  const mountedRef = useRef(true);
  const igIdRef    = useRef(null); // cached once — avoids redundant Step 1 calls

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!pageId || !token) {
      setState({ configured: false, loading: false });
      return;
    }

    async function poll() {
      try {
        if (!igIdRef.current) {
          igIdRef.current = await getInstagramAccountId(pageId, token);
        }
        const stats = await getInstagramStats(igIdRef.current, token);
        if (!mountedRef.current) return;
        setState({ configured: true, loading: false, ...stats });
      } catch (err) {
        if (!mountedRef.current) return;
        // Reset cached ID on auth errors so next poll retries the lookup
        if (err.message.includes("token") || err.message.includes("auth")) {
          igIdRef.current = null;
        }
        setState(prev => ({ ...prev, configured: false, loading: false, error: err.message }));
      }
    }

    poll();
    const timer = setInterval(poll, POLL_MS);
    return () => clearInterval(timer);
  }, [pageId, token]);

  return state;
}
